import type { DayType, NutritionGoal } from '@cadencia/contracts';
import { clampEnergyTarget, energyFloorKcal, SAFETY_LIMITS } from '../safety/limits.js';
import { roundTo } from '../units/round.js';

/**
 * Objetivo energetico y reparto de macros (docs/NUTRITION_ENGINE.md §3 y §4).
 *
 * Todo lo que sale de aqui ha pasado por `safety/limits.ts`. No hay ninguna
 * ruta que produzca un objetivo por debajo del suelo calorico o de la proteina
 * minima, venga de donde venga la peticion.
 */

export interface MacroTargets {
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
}

/**
 * Ajuste energetico por OBJETIVO NUTRICIONAL (§3). Fuente unica de estos
 * porcentajes.
 *
 * Hasta el 21/09/2026 se indexaba por la fase del bloque. Desde que la fase
 * describe la estructura del entrenamiento y no el balance energetico, la
 * nutricion recuperada (28/09/2026) lo indexa por `nutritionGoal`, que elige el
 * usuario. Las cifras son las mismas que tenian las fases del mismo nombre.
 */
export const NUTRITION_GOAL_ENERGY: Record<
  NutritionGoal,
  { minPct: number; maxPct: number; defaultPct: number }
> = {
  fat_loss: { minPct: -0.2, maxPct: -0.15, defaultPct: -0.175 },
  recomposition: { minPct: -0.1, maxPct: -0.05, defaultPct: -0.075 },
  maintenance: { minPct: 0, maxPct: 0, defaultPct: 0 },
  muscle_gain: { minPct: 0.05, maxPct: 0.1, defaultPct: 0.075 },
};

/** Coeficiente de proteina en g/kg por objetivo nutricional (§4.1). */
export const PROTEIN_COEF_BY_GOAL: Record<NutritionGoal, number> = {
  fat_loss: 2.2,
  recomposition: 2,
  maintenance: 1.8,
  muscle_gain: 1.8,
};

export const MACRO_RULES = {
  /** Grasa de partida antes de recortar por limites. */
  defaultFatGPerKg: 0.9,
  maxFatPctOfKcal: 0.35,
  /** Por debajo de esto se recorta grasa antes que dejar los HC tan bajos. */
  preferredMinCarbsGPerKg: 2.5,
} as const;

export interface EnergyTargetResult {
  kcal: number;
  /** Porcentaje efectivo aplicado sobre el TDEE, ya recortado. */
  appliedPct: number;
  reasonCodes: string[];
}

/**
 * Objetivo energetico diario medio para un objetivo nutricional.
 *
 * `requestedPct` permite pedir un punto concreto del rango del objetivo (por
 * ejemplo, aflojar el deficit tras una semana mala). Se recorta al rango del
 * objetivo primero y a los limites duros despues, en ese orden: el limite de
 * seguridad tiene que ser el ultimo en hablar.
 */
export function energyTargetKcal(input: {
  tdeeKcal: number;
  rmrKcal: number;
  goal: NutritionGoal;
  requestedPct?: number;
}): EnergyTargetResult {
  const rango = NUTRITION_GOAL_ENERGY[input.goal];
  const reasonCodes: string[] = [`goal_${input.goal}`];

  let pct = input.requestedPct ?? rango.defaultPct;
  if (pct < rango.minPct) {
    pct = rango.minPct;
    reasonCodes.push('clamped_goal_range');
  }
  if (pct > rango.maxPct) {
    pct = rango.maxPct;
    reasonCodes.push('clamped_goal_range');
  }

  const bruto = input.tdeeKcal * (1 + pct);
  const seguro = clampEnergyTarget(bruto, { tdeeKcal: input.tdeeKcal, rmrKcal: input.rmrKcal });
  reasonCodes.push(...seguro.reasonCodes);

  return {
    kcal: seguro.value,
    appliedPct: roundTo(seguro.value / input.tdeeKcal - 1, 4),
    reasonCodes,
  };
}

/**
 * Margen, en kcal, dentro del cual `kcalActual` y `kcal` se consideran iguales.
 *
 * No es una holgura de conveniencia: es el error que introducen los redondeos
 * que ya hacia este modulo. Los gramos se devuelven con un decimal (hasta 0,05
 * g de error: 0,2 kcal en proteina, 0,2 en hidratos y 0,45 en grasa) y hay dos
 * `Math.round` mas, el de `kcal` y el de `macrosToKcal`, que suman 1 kcal en el
 * peor caso. Total 1,85; se deja en 2. Sin este margen, una diferencia de una
 * kcal por redondeo se denunciaria como objetivo imposible.
 */
export const MACRO_FEASIBILITY_TOLERANCE_KCAL = 2;

export interface MacroResult {
  macros: MacroTargets;
  /**
   * kcal que suman DE VERDAD los gramos devueltos, por Atwater.
   *
   * `macros.kcal` es el objetivo que se PIDIO; esto es lo que hay en el plato.
   * Coinciden siempre que el problema tenga solucion, y cuando no la tiene la
   * diferencia es justo la magnitud del imposible (NUTR-003).
   */
  kcalActual: number;
  /**
   * false cuando los minimos duros de proteina y grasa ya gastan mas energia
   * que las kcal objetivo.
   *
   * No se resuelve recortando por debajo de los minimos —la prioridad sigue
   * siendo proteina y grasa minima por encima del objetivo calorico— pero
   * tampoco se disimula: hasta ahora se devolvian 242 g de proteina y 66 g de
   * grasa diciendo que eso eran 1200 kcal, cuando son 1562.
   */
  feasible: boolean;
  reasonCodes: string[];
}

/**
 * Reparto de macros (§4.1).
 *
 * Orden de prioridad ante conflicto: proteina >= grasa minima > carbohidratos.
 * Los carbohidratos son el residuo energetico, y se dejan caer antes que tocar
 * cualquiera de los otros dos.
 */
export function macroTargets(input: {
  kcalTarget: number;
  bodyWeightKg: number;
  goal: NutritionGoal;
  /** Sobrescribe el coeficiente de proteina del objetivo. Nunca baja del suelo. */
  proteinCoefOverride?: number;
  /** Multiplicador de grasa para modular el tipo de dia (§4.2). */
  fatFactor?: number;
}): MacroResult {
  const reasonCodes: string[] = [];
  const peso = input.bodyWeightKg;
  if (peso <= 0) throw new Error('Peso corporal invalido');

  const coefPedido = input.proteinCoefOverride ?? PROTEIN_COEF_BY_GOAL[input.goal];
  const coef = Math.max(coefPedido, SAFETY_LIMITS.macros.minProteinGPerKg);
  if (coef !== coefPedido) reasonCodes.push('protein_floor_applied');
  const proteinG = roundTo(coef * peso, 1);

  const minGrasaPorPeso = SAFETY_LIMITS.macros.minFatGPerKg * peso;
  const minGrasaPorKcal = (SAFETY_LIMITS.macros.minFatPctOfKcal * input.kcalTarget) / 9;
  const minGrasa = Math.max(minGrasaPorPeso, minGrasaPorKcal);
  const maxGrasa = (MACRO_RULES.maxFatPctOfKcal * input.kcalTarget) / 9;

  let fatG = MACRO_RULES.defaultFatGPerKg * peso * (input.fatFactor ?? 1);

  // El techo se aplica antes, pero puede quedar POR DEBAJO del suelo: con pocas
  // kcal y mucho peso, el 35 % da menos grasa que los 0,6 g/kg minimos. Cuando
  // pasa gana el suelo —es un limite de seguridad y el techo no— y entonces el
  // techo NO se ha respetado. Anunciar `fat_capped_pct_kcal` en ese caso decia
  // que las dos restricciones se habian cumplido, y solo se cumplio una.
  const techoAplicado = fatG > maxGrasa;
  if (techoAplicado) fatG = maxGrasa;
  if (fatG < minGrasa) {
    fatG = minGrasa;
    reasonCodes.push('fat_floor_applied');
    if (techoAplicado) reasonCodes.push('fat_cap_overridden_by_floor');
  } else if (techoAplicado) {
    reasonCodes.push('fat_capped_pct_kcal');
  }

  const carbsDe = (grasa: number): number =>
    (input.kcalTarget - 4 * proteinG - 9 * grasa) / 4;

  let carbsG = carbsDe(fatG);
  const carbsPreferidos = MACRO_RULES.preferredMinCarbsGPerKg * peso;

  // Antes de dejar los HC por los suelos se recorta la grasa hasta su minimo.
  // Nunca al reves: la grasa minima es un limite de seguridad y los HC no.
  if (carbsG < carbsPreferidos && fatG > minGrasa) {
    const grasaNecesaria = (input.kcalTarget - 4 * proteinG - 4 * carbsPreferidos) / 9;
    const nuevaGrasa = Math.max(minGrasa, Math.min(fatG, grasaNecesaria));
    if (nuevaGrasa < fatG) {
      fatG = nuevaGrasa;
      carbsG = carbsDe(fatG);
      reasonCodes.push('fat_reduced_for_carbs');
    }
  }

  if (carbsG < carbsPreferidos) reasonCodes.push('carbs_below_preferred');
  if (carbsG < 0) {
    carbsG = 0;
    reasonCodes.push('carbs_exhausted');
  }

  const macros: MacroTargets = {
    kcal: Math.round(input.kcalTarget),
    proteinG: roundTo(proteinG, 1),
    carbsG: roundTo(carbsG, 1),
    fatG: roundTo(fatG, 1),
  };

  // La factibilidad se mide sobre los gramos QUE SE DEVUELVEN, no sobre las
  // restricciones de entrada. Asi la deteccion no depende de conocer de
  // antemano cuales son los limites duros ni de reproducir su formula: si
  // manana se añade otro minimo, esto lo sigue viendo.
  const kcalActual = macrosToKcal(macros);
  const feasible = Math.abs(kcalActual - macros.kcal) <= MACRO_FEASIBILITY_TOLERANCE_KCAL;
  if (!feasible) reasonCodes.push('macros_infeasible');

  return { macros, kcalActual, feasible, reasonCodes };
}

/** kcal que suman unos macros, por Atwater. */
export function macrosToKcal(macros: Omit<MacroTargets, 'kcal'>): number {
  return Math.round(4 * macros.proteinG + 4 * macros.carbsG + 9 * macros.fatG);
}

// --------------------------------------------------------------------------
// Tipos de dia (§4.2)
// --------------------------------------------------------------------------

/**
 * Modulacion por tipo de dia. Solo se declaran kcal y grasa: los HC salen del
 * residuo energetico, asi que su porcentaje de la tabla del §4.2 es una
 * CONSECUENCIA, no una entrada. Declararlo como entrada permitiria pedir
 * combinaciones que no suman las kcal del dia.
 */
export const DAY_TYPE_MODULATION: Record<DayType, { kcalPct: number; fatPct: number }> = {
  strength_high: { kcalPct: 0.08, fatPct: -0.15 },
  strength: { kcalPct: 0.04, fatPct: -0.08 },
  cardio: { kcalPct: 0, fatPct: 0 },
  rest: { kcalPct: -0.1, fatPct: 0.1 },
};

/** Tolerancia de cierre de la semana (§4.2). */
export const WEEKLY_CLOSURE_TOLERANCE = 0.02;

export interface DayTarget {
  dayType: DayType;
  macros: MacroTargets;
  /** kcal que suman de verdad los gramos del dia (NUTR-003). */
  kcalActual: number;
  /** false si los minimos duros no caben en las kcal del dia (NUTR-003). */
  feasible: boolean;
  reasonCodes: string[];
}

export interface WeeklyTargets {
  days: DayTarget[];
  weeklyKcal: number;
  targetWeeklyKcal: number;
  /** Suelo energetico que se ha exigido a cada dia. */
  energyFloorKcal: number;
  reasonCodes: string[];
}

/**
 * Reparte el objetivo semanal entre los siete dias segun su tipo (§4.2) y
 * comprueba el cierre de la semana.
 *
 * La media semanal es lo que manda; los dias son una redistribucion, no siete
 * objetivos independientes. Si la combinacion de tipos se desvia mas del ±2 %,
 * se corrige sobre los dias de descanso, que son los que menos sufren un
 * ajuste, y si no hay ninguno se reparte entre todos.
 *
 * EL SUELO ES DE CADA DIA, NO DE LA MEDIA (NUTR-001).
 *
 * `energyTargetKcal` recorta la MEDIA al suelo y hasta ahi todo era correcto;
 * lo que pasaba despues es que la modulacion por tipo de dia restaba un 10 % a
 * los dias de descanso y nadie volvia a mirar. Con la media clavada en el suelo
 * —el caso normal de un deficit agresivo, no uno raro— los dias de descanso
 * salian por debajo: con suelo 1600, a 1440 kcal. El invariante que este mismo
 * fichero declara en su cabecera decia que eso no podia ocurrir.
 *
 * Por eso hace falta `rmrKcal`: el suelo depende del RMR y sin el este modulo
 * no puede saber donde esta. Es OBLIGATORIO a proposito. Un parametro opcional
 * dejaria que un llamador se saltara el invariante sin enterarse, que es
 * exactamente como se llego hasta aqui.
 *
 * ORDEN DE PRIORIDAD ante conflicto: suelo diario > cierre semanal >
 * porcentaje de modulacion. Ningun dia baja del suelo para cuadrar el total.
 */
export function weeklyTargets(input: {
  dailyMeanKcal: number;
  dayTypes: readonly DayType[];
  bodyWeightKg: number;
  goal: NutritionGoal;
  /** RMR vigente. Sin el no se sabe donde esta el suelo de cada dia. */
  rmrKcal: number;
}): WeeklyTargets {
  if (input.dayTypes.length === 0) throw new Error('Una semana sin dias no se puede repartir');

  const objetivoSemanal = input.dailyMeanKcal * input.dayTypes.length;
  const reasonCodes: string[] = [];
  const suelo = energyFloorKcal(input.rmrKcal);

  const kcalPorDia = input.dayTypes.map(
    (tipo) => input.dailyMeanKcal * (1 + DAY_TYPE_MODULATION[tipo].kcalPct),
  );

  const suma = kcalPorDia.reduce((a, b) => a + b, 0);
  const desvio = (suma - objetivoSemanal) / objetivoSemanal;

  if (Math.abs(desvio) > WEEKLY_CLOSURE_TOLERANCE) {
    const exceso = suma - objetivoSemanal;
    const indicesDescanso = input.dayTypes
      .map((tipo, indice) => (tipo === 'rest' ? indice : -1))
      .filter((indice) => indice >= 0);
    const destinos = indicesDescanso.length > 0 ? indicesDescanso : kcalPorDia.map((_, i) => i);
    const reparto = exceso / destinos.length;
    for (const indice of destinos) kcalPorDia[indice] = kcalPorDia[indice]! - reparto;
    reasonCodes.push(
      indicesDescanso.length > 0 ? 'weekly_closure_on_rest_days' : 'weekly_closure_spread',
    );
  }

  // --- Suelo por dia. Es lo ultimo que habla sobre las kcal. -----------------
  const subidosAlSuelo = new Set<number>();
  for (const [indice, kcal] of kcalPorDia.entries()) {
    if (kcal < suelo) {
      kcalPorDia[indice] = suelo;
      subidosAlSuelo.add(indice);
    }
  }

  if (subidosAlSuelo.size > 0) {
    reasonCodes.push('daily_energy_floor_applied');

    // Subir dias mete energia que la semana no habia pedido. Se devuelve
    // quitandosela a los dias que TIENEN de donde, en proporcion a su margen
    // sobre el suelo: en una pasada, sin iterar, y sin depender del orden del
    // array. La regla de «corregir sobre los descansos» no sirve aqui porque
    // los descansos son justamente los que se acaban de clavar en el suelo.
    const exceso = kcalPorDia.reduce((a, b) => a + b, 0) - objetivoSemanal;
    if (exceso > 0) {
      const margen = kcalPorDia.map((kcal) => Math.max(0, kcal - suelo));
      const margenTotal = margen.reduce((a, b) => a + b, 0);

      if (margenTotal >= exceso) {
        // Cada dia cede como mucho su propio margen, asi que ninguno cruza el
        // suelo por debajo.
        for (const [indice, m] of margen.entries()) {
          kcalPorDia[indice] = kcalPorDia[indice]! - exceso * (m / margenTotal);
        }
      } else {
        // La semana NO cabe: ni quitando todo el margen se llega al objetivo.
        // Gana el suelo y el total queda por encima del objetivo, dicho con un
        // codigo en vez de falsificando el numero.
        for (const indice of kcalPorDia.keys()) kcalPorDia[indice] = suelo;
        reasonCodes.push('weekly_closure_limited_by_floor');
      }
    }
  }

  const days: DayTarget[] = input.dayTypes.map((tipo, indice) => {
    const resultado = macroTargets({
      kcalTarget: kcalPorDia[indice]!,
      bodyWeightKg: input.bodyWeightKg,
      goal: input.goal,
      fatFactor: 1 + DAY_TYPE_MODULATION[tipo].fatPct,
    });
    return {
      dayType: tipo,
      macros: resultado.macros,
      kcalActual: resultado.kcalActual,
      feasible: resultado.feasible,
      reasonCodes: [
        `day_type_${tipo}`,
        ...(subidosAlSuelo.has(indice) ? ['clamped_energy_floor_day'] : []),
        ...resultado.reasonCodes,
      ],
    };
  });

  return {
    days,
    weeklyKcal: days.reduce((total, dia) => total + dia.macros.kcal, 0),
    targetWeeklyKcal: Math.round(objetivoSemanal),
    energyFloorKcal: suelo,
    reasonCodes,
  };
}

/**
 * Tipo de dia que corresponde a una jornada segun lo que hay planificado.
 * `strength_high` se reserva a las sesiones de tren inferior o de carga alta,
 * que son las que justifican mas hidratos (§4.2).
 */
export function dayTypeFor(input: {
  hasStrengthSession: boolean;
  isLowerBodyOrHeavy: boolean;
  hasCardio: boolean;
}): DayType {
  if (input.hasStrengthSession) return input.isLowerBodyOrHeavy ? 'strength_high' : 'strength';
  if (input.hasCardio) return 'cardio';
  return 'rest';
}
